import { useCallback } from 'react';

import {
    Modal as MagritteModal,
    BottomSheet,
    BottomSheetFooter,
    NavigationBar,
    Button,
    Checkbox,
    Text,
    VSpacing,
    UncontrolledInput,
    Action,
    ActionBar,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import FavoritesButton from 'src/components/ResumeCard/Actions/SaveToFavoritesButton/Button';
import Folder from 'src/components/ResumeFolders/Folder';
import { useResumeFolder } from 'src/components/ResumeFolders/useResumeFolder';
import translation from 'src/components/translation';
import type { FoldersGroupType } from 'src/models/resumeFolders';

import type { SaveToFavoritesProps } from 'src/components/ResumeCard/Actions/SaveToFavoritesButton/Menu/types';

import styles from './index.less';

const TrlKeys = {
    title: 'resume.search.favorite',
    myFolders: 'employer.resumeFolders.myFolders',
    sharedFolders: 'employer.resumeFolders.sharedFolders',
    createFolder: 'employer.resumeFolders.createFolder',
    newFolder: 'employer.resumeFolders.newFolder',
    save: 'Save',
    cancel: 'Cancel',
};

const SaveToFoldersMenuMobile: TranslatedComponent<SaveToFavoritesProps> = ({
    isVisible,
    setIsVisible,
    dataQAPrefix,
    folders,
    foldersIdList,
    isSaved,
    onClick,
    onSave,
    trls,
    closeAdditionalMenu,
    isMobile,
}) => {
    const {
        onSubmit,
        onChangeAddFolderName,
        onChangeFolders,
        onChangeAddFolder,
        getSortedFolders,
        checkedFolders,
        isCheckedAddFolder,
        isLoading,
    } = useResumeFolder({ folders, selectedIdsFolder: foldersIdList, onSave });

    const makeSortedFolders = useCallback(
        (folders: FoldersGroupType = [], selectedIdsFolder: string[]) => {
            return getSortedFolders(folders).map((id) => (
                <Folder
                    CheckElement={Checkbox}
                    key={id}
                    id={String(id)}
                    name={folders?.[id]?.name || ''}
                    selected={selectedIdsFolder.includes(String(id))}
                    onChange={onChangeFolders}
                    dataQAPrefix={`${dataQAPrefix}-item`}
                    isMagritte
                />
            ));
        },
        [dataQAPrefix, getSortedFolders, onChangeFolders]
    );

    const handleClose = useCallback(() => {
        setIsVisible(false);
        closeAdditionalMenu?.();
    }, [closeAdditionalMenu, setIsVisible]);

    const handleSubmit = useCallback(() => {
        onSubmit();
        closeAdditionalMenu?.();
    }, [onSubmit, closeAdditionalMenu]);

    const hasMyFolders = Object.keys(folders.own).length > 0;
    const hasSharedFolders = Object.keys(folders.shared).length > 0;

    const content = (
        <>
            {hasMyFolders && (
                <>
                    <Text typography="subtitle-2-semibold">{trls[TrlKeys.myFolders]}</Text>
                    <VSpacing default={4} />
                    <ul>{makeSortedFolders(folders.own, checkedFolders.current)}</ul>
                    <VSpacing default={16} />
                </>
            )}
            {hasSharedFolders && (
                <>
                    <Text typography="subtitle-2-semibold">{trls[TrlKeys.sharedFolders]}</Text>
                    <VSpacing default={4} />
                    <ul>{makeSortedFolders(folders.shared, checkedFolders.current)}</ul>
                    <VSpacing default={16} />
                </>
            )}
            <Text typography="subtitle-2-semibold">{trls[TrlKeys.createFolder]}</Text>
            <VSpacing default={8} />
            <div className={styles.newFolderWrapper}>
                <Checkbox
                    name="favoriteFolder"
                    onChange={onChangeAddFolder}
                    checked={isCheckedAddFolder}
                    dataQaCheckbox={`${dataQAPrefix}-new-checkbox`}
                />
                <UncontrolledInput
                    size="medium"
                    onChange={(value) => {
                        onChangeAddFolderName(value);
                    }}
                    data-qa={`${dataQAPrefix}-field`}
                    placeholder={trls[TrlKeys.newFolder]}
                    clearableOnFocus={false}
                />
            </div>
            <VSpacing default={16} />
        </>
    );

    return (
        <>
            <FavoritesButton isMobile isSaved={isSaved} onClick={onClick} closeAdditionalMenu={closeAdditionalMenu} />
            {isMobile ? (
                <BottomSheet
                    visible={isVisible}
                    onClose={handleClose}
                    header={<NavigationBar title={trls[TrlKeys.title]} />}
                    footer={
                        <BottomSheetFooter>
                            <Button
                                mode="primary"
                                style="accent"
                                onClick={handleSubmit}
                                loading={isLoading}
                                data-qa={`${dataQAPrefix}-save`}
                                stretched
                            >
                                {trls[TrlKeys.save]}
                            </Button>
                            <Button mode="secondary" style="accent" onClick={() => setIsVisible(false)} stretched>
                                {trls[TrlKeys.cancel]}
                            </Button>
                        </BottomSheetFooter>
                    }
                >
                    {content}
                </BottomSheet>
            ) : (
                <MagritteModal
                    data-qa="non-appearance-modal"
                    title={trls[TrlKeys.title]}
                    footer={
                        <ActionBar
                            primaryActions={
                                <Button
                                    mode="primary"
                                    style="accent"
                                    onClick={handleSubmit}
                                    loading={isLoading}
                                    data-qa={`${dataQAPrefix}-save`}
                                >
                                    {trls[TrlKeys.save]}
                                </Button>
                            }
                            secondaryActions={
                                <Button
                                    data-qa="non-appearance-form__close"
                                    mode="secondary"
                                    style="accent"
                                    onClick={handleClose}
                                >
                                    {trls[TrlKeys.cancel]}
                                </Button>
                            }
                        />
                    }
                    size="small"
                    visible={isVisible}
                    onClose={handleClose}
                    actions={<Action mode="secondary" onClick={handleClose} icon={CrossOutlinedSize24} />}
                >
                    {content}
                </MagritteModal>
            )}
        </>
    );
};

export default translation(SaveToFoldersMenuMobile);
